import { lazy, Suspense, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ContactModal } from "@/components/ui/ContactModal";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { UserProfileProvider } from "@/hooks/useUserProfile";
import { ContactModalProvider } from "@/hooks/useContactModal";
import { CorporateAuthProvider } from "@/contexts/CorporateAuthContext";
import { AINotesSocketProvider } from "@/contexts/AINotesSocketContext";
import { useAutoTokenRefresh } from "@/hooks/useAutoTokenRefresh";
import { useCacheSync } from "@/hooks/useCacheSync";
import ProtectedRoute from "./components/ProtectedRoute";
import { FeatureGuard } from "./components/guards/FeatureGuard";
import FeedbackButton from "./components/FeedbackButton";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";

// Lazy load all page components to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const CardEditor = lazy(() => import("./pages/CardEditor"));
const CardView = lazy(() => import("./pages/CardView"));
const VisitorCardRedirect = lazy(() => import("./pages/VisitorCardRedirect"));
const VisitorCardPage = lazy(() => import("./pages/VisitorCardPage"));
const CampaignCardPage = lazy(() => import("./pages/CampaignCardPage"));
const QRDesigner = lazy(() => import("./pages/QRDesigner"));
const QRDesignerPublic = lazy(() => import("./pages/QRDesignerPublic"));
const Analytics = lazy(() => import("./pages/Analytics"));
const QRWallet = lazy(() => import("./pages/QRWallet"));
const QRShare = lazy(() => import("./pages/QRShare"));
const VirtualBackground = lazy(() => import("./pages/VirtualBackground"));
const MyQRStore = lazy(() => import("./pages/MyQRStore"));
const LeadForms = lazy(() => import("./pages/LeadForms"));
const LeadSubmissions = lazy(() => import("./pages/LeadSubmissions"));
const QRFormInterstitial = lazy(() => import("./pages/QRFormInterstitial"));
const Settings = lazy(() => import("./pages/Settings"));
const Appearance = lazy(() => import("./pages/Appearance"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UseCases = lazy(() => import("./pages/UseCases"));
const Features = lazy(() => import("./pages/Features"));
const Business = lazy(() => import("./pages/Business"));
const FAQ = lazy(() => import("./pages/FAQ"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Blog = lazy(() => import("./pages/Blog"));
const HowTo = lazy(() => import("./pages/HowTo"));
const HowToVideos = lazy(() => import("@/pages/HowToVideos"));
const Support = lazy(() => import("./pages/Support"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));
const SeoPage = lazy(() => import("./pages/SeoPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));

// Dedicated SEO Landing Pages
const DigitalBusinessCardPage = lazy(() => import("./pages/seo/DigitalBusinessCardPage"));
const QRCodeGeneratorPage = lazy(() => import("./pages/seo/QRCodeGeneratorPage"));

// Content pages
const CanvaQRCodeGeneratorPage = lazy(() => import("./pages/content/CanvaQRCodeGeneratorPage"));
const BusinessCardGeneratorPage = lazy(() => import("./pages/content/BusinessCardGeneratorPage"));
const WaveConnectAlternativePage = lazy(() => import("./pages/content/WaveConnectAlternativePage"));
const QRCodeChimpAlternativePage = lazy(() => import("./pages/content/QRCodeChimpAlternativePage"));
const EmailSignatureGeneratorPage = lazy(() => import("./pages/content/EmailSignatureGeneratorPage"));
const HowToScanQRCodeIphonePage = lazy(() => import("./pages/content/HowToScanQRCodeIphonePage"));
const HowToScanQRCodeAndroidPage = lazy(() => import("./pages/content/HowToScanQRCodeAndroidPage"));
const ConnectikoVsQRCodeMonkeyPage = lazy(() => import("./pages/content/ConnectikoVsQRCodeMonkeyPage"));
const OpenHousePaperToCRMPage = lazy(() => import("./pages/content/OpenHousePaperToCRMPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));

// ── New public pages (Phase 1 stubs → replaced with full content in Phase 4) ──
// Platform pages
const PlatformQR = lazy(() => import("./pages/platform/PlatformQR"));
const PlatformForms = lazy(() => import("./pages/platform/PlatformForms"));
const PlatformCards = lazy(() => import("./pages/platform/PlatformCards"));
const PlatformCRM = lazy(() => import("./pages/platform/PlatformCRM"));
const PlatformAINotes = lazy(() => import("./pages/platform/PlatformAINotes"));
const PlatformAnalytics = lazy(() => import("./pages/platform/PlatformAnalytics"));
// Solutions pages
const SolutionsCapture = lazy(() => import("./pages/solutions/SolutionsCapture"));
const SolutionsHVAC = lazy(() => import("./pages/solutions/SolutionsHVAC"));
const SolutionsRealEstate = lazy(() => import("./pages/solutions/SolutionsRealEstate"));
const SolutionsEvents = lazy(() => import("./pages/solutions/SolutionsEvents"));
const SolutionsRetail = lazy(() => import("./pages/solutions/SolutionsRetail"));
const SolutionsTeams = lazy(() => import("./pages/solutions/SolutionsTeams"));
// Auxiliary pages
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Contact = lazy(() => import("./pages/Contact"));
const Careers = lazy(() => import("./pages/Careers"));
const CustomSolutions = lazy(() => import("./pages/CustomSolutions"));

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDashboardLogin = lazy(() => import("./pages/AdminDashboardLogin"));
const DataControl = lazy(() => import("./pages/DataControl"));
const AINotesPage = lazy(() => import("./pages/ai-notes/AINotesPage"));
const CRMIntegrations = lazy(() => import("./pages/CRMIntegrations"));
const EventsList = lazy(() => import("./pages/EventsList"));
const EventSetupWizard = lazy(() => import("./pages/EventSetupWizard"));
const EventDashboard = lazy(() => import("./pages/EventDashboard"));
const EventAccountAccept = lazy(() => import("./pages/events/EventAccountAccept"));
const EventManagerAccept = lazy(() => import("./pages/events/EventManagerAccept"));
const EventAccountPayment = lazy(() => import("./pages/events/EventAccountPayment"));
const EventBadgeView = lazy(() => import("./pages/events/EventBadgeView"));
const CheckinKiosk = lazy(() => import("./pages/CheckinKiosk"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const Team = lazy(() => import("./pages/Team"));
const TeamManagement = lazy(() => import("./pages/TeamManagement"));
const TeamAnalytics = lazy(() => import("./pages/TeamAnalytics"));
const TeamJoinByCode = lazy(() => import("./pages/TeamJoinByCode"));
const TeamInvitation = lazy(() => import("./pages/TeamInvitation"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const CorporateAuth = lazy(() => import("./pages/CorporateAuth"));
const CorporateDashboard = lazy(() => import("./components/corporate/CorporateManagementDashboard"));

// Enterprise pages
const EnterpriseActivate = lazy(() => import("./pages/enterprise/EnterpriseActivate"));
const EnterprisePendingApproval = lazy(() => import("./pages/enterprise/EnterprisePendingApproval"));
const EnterprisePaymentSuccess = lazy(() => import("./pages/enterprise/EnterprisePaymentSuccess"));
const EnterprisePaymentCancelled = lazy(() => import("./pages/enterprise/EnterprisePaymentCancelled"));
const EnterprisePaymentPage = lazy(() => import("./pages/enterprise/EnterprisePaymentPage"));
const EnterprisePaymentPending = lazy(() => import("./pages/enterprise/EnterprisePaymentPending"));
const EnterpriseSetPassword = lazy(() => import("./pages/enterprise/EnterpriseSetPassword"));

const EmailPage = lazy(() => import("./pages/email/EmailPage"));
const EmailSettings = lazy(() => import("./pages/email/EmailSettings"));
const EmailCompose = lazy(() => import("./pages/email/EmailCompose"));
const EmailHistory = lazy(() => import("./pages/email/EmailHistory"));
const EmailSubscribers = lazy(() => import("./pages/email/EmailSubscribers"));
const EmailUpgradeWall = lazy(() => import("./pages/email/EmailUpgradeWall"));const EmailSignaturePage = lazy(() => import('./pages/EmailSignaturePage'));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reduce unnecessary refetches
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      // Keep data fresh for 5 minutes to avoid duplicate requests
      staleTime: 5 * 60 * 1000,
      // Cache data for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests but with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

const CANONICAL_ORIGIN = "https://connectiko.io";

// App content component to use hooks
const AppContent = () => {
  const location = useLocation();
  const [isImpersonating] = useState(() => !!sessionStorage.getItem('impersonation_active'));

  // Auto-refresh JWT tokens 60 minutes before expiration
  useAutoTokenRefresh(60);

  // Initialize real-time cache synchronization
  const cacheSync = useCacheSync(queryClient);
  
  // Log cache sync status (only in development)
  if (import.meta.env.DEV && cacheSync.isConnected) {
    console.log('✅ Real-time cache sync active');
  }

  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const canonicalUrl = `${CANONICAL_ORIGIN}${normalizedPath}`;

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      {isImpersonating && <ImpersonationBanner />}
      <Toaster />
      <Sonner />
      <ContactModal />
      {!normalizedPath.startsWith('/events/badge') && <FeedbackButton />}
      <Suspense fallback={
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontSize: '18px',
          fontFamily: 'system-ui, sans-serif'
        }}>
          Loading...
        </div>
      }>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/business" element={<Business />} />
          <Route path="/use-cases" element={<UseCases />} />
          <Route path="/features" element={<Features />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/subscription/success" element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute>
              <SubscriptionManagement />
            </ProtectedRoute>
          } />
          <Route path="/how-to" element={<HowTo />} />
          <Route path="/how-to-videos" element={<HowToVideos />} />
          <Route path="/how-to/:guideId" element={<GuideDetail />} />
          <Route path="/support" element={<Support />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/blog" element={<Blog />} />

          {/* ── New public routes (Phase 1 stubs → Phase 4 full content) ── */}
          {/* Platform */}
          <Route path="/platform/qr"           element={<PlatformQR />} />
          <Route path="/platform/forms"        element={<PlatformForms />} />
          <Route path="/platform/cards"        element={<PlatformCards />} />
          <Route path="/platform/crm"          element={<PlatformCRM />} />
          <Route path="/platform/ai-notes"     element={<PlatformAINotes />} />
          <Route path="/platform/analytics"    element={<PlatformAnalytics />} />
          {/* Solutions */}
          <Route path="/solutions/capture"     element={<SolutionsCapture />} />
          <Route path="/solutions/hvac"        element={<SolutionsHVAC />} />
          <Route path="/solutions/real-estate" element={<SolutionsRealEstate />} />
          <Route path="/solutions/events"      element={<SolutionsEvents />} />
          <Route path="/solutions/retail"      element={<SolutionsRetail />} />
          <Route path="/solutions/teams"       element={<SolutionsTeams />} />
          {/* Auxiliary */}
          <Route path="/how-it-works"          element={<HowItWorks />} />
          <Route path="/contact"               element={<Contact />} />
          <Route path="/careers"               element={<Careers />} />
          <Route path="/custom-solutions"      element={<CustomSolutions />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/corporate-auth" element={<CorporateAuth />} />
          <Route path="/auth/corporate" element={<CorporateAuth />} />
          <Route path="/corporate/join" element={<CorporateAuth />} />
          <Route path="/teams/join" element={<TeamJoinByCode />} />
          <Route path="/teams/join/:token" element={<TeamInvitation />} />
          <Route path="/invite/accept" element={<AcceptInvite />} />
          <Route path="/corporate/dashboard" element={
            <ProtectedRoute
              requireAccountTypes={['corporate', 'business', 'enterprise']}
              requireAccountRoles={['super_admin', 'corporate_owner', 'admin', 'manager']}
            >
              <CorporateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/corporate/onboarding" element={
            <ProtectedRoute
              requireAccountTypes={['corporate', 'business', 'enterprise']}
              requireAccountRoles={['super_admin', 'corporate_owner', 'admin', 'manager']}
            >
              <CorporateDashboard />
            </ProtectedRoute>
          } />
          
          {/* Enterprise Activation Routes */}
          <Route path="/enterprise/activate" element={<EnterpriseActivate />} />
          <Route path="/enterprise/pending-approval" element={<EnterprisePendingApproval />} />
          <Route path="/enterprise/payment-success" element={<EnterprisePaymentSuccess />} />
          <Route path="/enterprise/payment-cancelled" element={<EnterprisePaymentCancelled />} />
          <Route path="/enterprise/payment" element={<EnterprisePaymentPage />} />
          <Route path="/enterprise/payment-pending" element={<EnterprisePaymentPending />} />
          <Route path="/enterprise/set-password" element={<EnterpriseSetPassword />} />
          
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/c/visitor-card" element={<VisitorCardRedirect />} />
          <Route path="/c/visitor-card/:token" element={<VisitorCardPage />} />
          <Route path="/c/card/:token" element={<CampaignCardPage />} />
          <Route path="/c/:slug" element={<CardView />} />
          <Route path="/dashboard" element={<Navigate to="/card" replace />} />
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/signin" element={<Navigate to="/auth" replace />} />
          <Route path="/card/:slug" element={
            <CardView />
          } />
          <Route path="/card" element={
            <ProtectedRoute>
              <CardEditor />
            </ProtectedRoute>
          } />
          <Route path="/qr-designer" element={
            <ProtectedRoute>
              <QRDesigner />
            </ProtectedRoute>
          } />
          <Route path="/qr-designer-visitor" element={<QRDesignerPublic />} />
          <Route path="/qr-store" element={
            <ProtectedRoute>
              <MyQRStore />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <FeatureGuard>
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            </FeatureGuard>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute>
              <QRWallet />
            </ProtectedRoute>
          } />
          <Route path="/qrshare" element={
            <ProtectedRoute>
              <QRShare />
            </ProtectedRoute>
          } />
          <Route path="/virtual-background" element={
            <ProtectedRoute>
              <VirtualBackground />
            </ProtectedRoute>
          } />
          <Route path="/forms" element={
            <ProtectedRoute>
              <LeadForms />
            </ProtectedRoute>
          } />
          <Route path="/qr-form/:shortCode" element={<QRFormInterstitial />} />
          <Route path="/qr-form/:profileId/:qrCodeId" element={<QRFormInterstitial />} />
          <Route path="/leads" element={
            <ProtectedRoute>
              <LeadSubmissions />
            </ProtectedRoute>
          } />
          <Route path="/crm-integrations" element={
            <ProtectedRoute>
              <CRMIntegrations />
            </ProtectedRoute>
          } />
          <Route path="/events" element={
            <ProtectedRoute>
              <EventsList />
            </ProtectedRoute>
          } />
          <Route path="/events/new" element={
            <ProtectedRoute>
              <EventSetupWizard />
            </ProtectedRoute>
          } />
          <Route path="/events/:eventId/dashboard" element={
            <ProtectedRoute>
              <EventDashboard />
            </ProtectedRoute>
          } />
          {/* Event package invite/payment — public accept links + authenticated billing page */}
          <Route path="/events/accept" element={<EventAccountAccept />} />
          <Route path="/events/managers/accept" element={<EventManagerAccept />} />
          <Route path="/events/payment" element={
            <ProtectedRoute>
              <EventAccountPayment />
            </ProtectedRoute>
          } />
          {/* Staff check-in kiosk — no Connectiko account, gated by the event's own staff PIN instead */}
          <Route path="/checkin/:eventId" element={<CheckinKiosk />} />
          {/* Attendee's own digital badge — public, no Connectiko account */}
          <Route path="/events/badge/:token" element={<EventBadgeView />} />

          {/* Email campaigns — gated by plan; personal users see paywall */}
          <Route path="/email" element={<ProtectedRoute><EmailPage /></ProtectedRoute>}>
            <Route index element={<EmailHistory />} />
            <Route path="settings" element={<EmailSettings />} />
            <Route path="compose" element={<EmailCompose />} />
            <Route path="campaigns" element={<EmailHistory />} />
            <Route path="subscribers" element={<EmailSubscribers />} />
          </Route>
          <Route path="/email/upgrade" element={<ProtectedRoute><EmailUpgradeWall /></ProtectedRoute>} />
          <Route path="/email-signature" element={<ProtectedRoute><EmailSignaturePage /></ProtectedRoute>} />
          <Route path="/ai-notes" element={
            <FeatureGuard>
              <ProtectedRoute>
                <AINotesPage />
              </ProtectedRoute>
            </FeatureGuard>
          } />
          <Route path="/team" element={
            <ProtectedRoute 
              requireTeamMembership={false}
              checkTeamRoles={true}
              allowedRoles={['super_admin', 'corporate_owner', 'admin', 'manager', 'member']}
            >
              <Team />
            </ProtectedRoute>
          } />
          <Route path="/team/:teamId" element={
            <ProtectedRoute 
              requireTeamMembership={false}
              checkTeamRoles={true}
              allowedRoles={['super_admin', 'corporate_owner', 'admin', 'manager', 'member']}
            >
              <TeamManagement />
            </ProtectedRoute>
          } />
          <Route path="/team/:teamId/analytics" element={
            <ProtectedRoute 
              requireTeamMembership={false}
              checkTeamRoles={true}
              allowedRoles={['super_admin', 'corporate_owner', 'admin', 'manager']}
            >
              <TeamAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/appearance" element={
            <ProtectedRoute>
              <Appearance />
            </ProtectedRoute>
          } />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/data-control" element={<DataControl />} />
          {/* Unified Admin Dashboard */}
          <Route path="/admin/login" element={<AdminDashboardLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          
          {/* Dedicated SEO Landing Pages - Must be before /:slug catch-all */}
          <Route path="/digital-business-card" element={<DigitalBusinessCardPage />} />
          <Route path="/qr-code-generator" element={<QRCodeGeneratorPage />} />

          {/* Content pages */}
          <Route path="/canva-qr-code-generator" element={<CanvaQRCodeGeneratorPage />} />
          <Route path="/business-card-generator" element={<BusinessCardGeneratorPage />} />
          <Route path="/wave-connect-alternative" element={<WaveConnectAlternativePage />} />
          <Route path="/qrcodechimp-alternative" element={<QRCodeChimpAlternativePage />} />
          <Route path="/email-signature-generator" element={<EmailSignatureGeneratorPage />} />
          <Route path="/how-to-scan-a-qr-code-on-iphone" element={<HowToScanQRCodeIphonePage />} />
          <Route path="/how-to-scan-a-qr-code-on-android" element={<HowToScanQRCodeAndroidPage />} />
          <Route path="/connectiko-vs-qr-code-monkey" element={<ConnectikoVsQRCodeMonkeyPage />} />
          <Route path="/open-house-paper-to-crm" element={<OpenHousePaperToCRMPage />} />

          {/* Blog articles (data-driven, single shared template) */}
          <Route path="/connectiko-for-agencies" element={<BlogArticlePage slug="connectiko-for-agencies" />} />
          <Route path="/connectiko-for-contractors" element={<BlogArticlePage slug="connectiko-for-contractors" />} />
          <Route path="/connectiko-for-franchises" element={<BlogArticlePage slug="connectiko-for-franchises" />} />
          <Route path="/connectiko-for-home-services" element={<BlogArticlePage slug="connectiko-for-home-services" />} />
          <Route path="/connectiko-for-semiconductor-industry" element={<BlogArticlePage slug="connectiko-for-semiconductor-industry" />} />
          <Route path="/connectiko-for-trade-shows" element={<BlogArticlePage slug="connectiko-for-trade-shows" />} />
          <Route path="/digital-business-card-alternatives" element={<BlogArticlePage slug="digital-business-card-alternatives" />} />
          <Route path="/digital-business-card-for-teams" element={<BlogArticlePage slug="digital-business-card-for-teams" />} />
          <Route path="/digital-business-card-vs-paper-business-card" element={<BlogArticlePage slug="digital-business-card-vs-paper-business-card" />} />
          <Route path="/dynamic-vs-static-qr-codes" element={<Navigate to="/dynamic-vs-static-qr-code" replace />} />
          <Route path="/event-leads-to-crm" element={<BlogArticlePage slug="event-leads-to-crm" />} />
          <Route path="/linq-alternatives" element={<BlogArticlePage slug="linq-alternatives" />} />
          <Route path="/popl-alternatives" element={<BlogArticlePage slug="popl-alternatives" />} />
          <Route path="/qr-code-generator-alternatives" element={<BlogArticlePage slug="qr-code-generator-alternatives" />} />
          <Route path="/digital-business-card-for-healthcare-professionals" element={<BlogArticlePage slug="digital-business-card-for-healthcare-professionals" />} />
          <Route path="/digital-business-card-for-financial-advisors" element={<BlogArticlePage slug="digital-business-card-for-financial-advisors" />} />
          <Route path="/digital-business-card-for-attorneys-and-law-firms" element={<BlogArticlePage slug="digital-business-card-for-attorneys-and-law-firms" />} />
          <Route path="/digital-business-card-for-freelancers" element={<BlogArticlePage slug="digital-business-card-for-freelancers" />} />
          <Route path="/digital-business-card-for-small-business" element={<BlogArticlePage slug="digital-business-card-for-small-business" />} />
          <Route path="/how-to-create-a-digital-business-card" element={<BlogArticlePage slug="how-to-create-a-digital-business-card" />} />
          <Route path="/how-to-share-a-digital-business-card" element={<BlogArticlePage slug="how-to-share-a-digital-business-card" />} />
          <Route path="/qr-code-lead-capture-for-businesses" element={<Navigate to="/qr-code-lead-capture" replace />} />
          <Route path="/qr-code-lead-capture-for-trade-shows" element={<BlogArticlePage slug="qr-code-lead-capture-for-trade-shows" />} />
          <Route path="/qr-code-lead-capture" element={<BlogArticlePage slug="qr-code-lead-capture" />} />
          <Route path="/qr-code-lead-generation" element={<Navigate to="/qr-code-lead-capture" replace />} />
          <Route path="/qr-code-to-hubspot-lead" element={<BlogArticlePage slug="qr-code-to-hubspot-lead" />} />
          <Route path="/qr-code-tracking-explained" element={<Navigate to="/qr-code-tracking" replace />} />
          <Route path="/trade-show-lead-capture-software" element={<BlogArticlePage slug="trade-show-lead-capture-software" />} />
          <Route path="/what-is-a-qr-code" element={<BlogArticlePage slug="what-is-a-qr-code" />} />
          <Route path="/canva-business-card-alternative" element={<BlogArticlePage slug="canva-business-card-alternative" />} />
          <Route path="/canva-pricing" element={<BlogArticlePage slug="canva-pricing" />} />
          <Route path="/canva-qr-code-customization" element={<BlogArticlePage slug="canva-qr-code-customization" />} />
          <Route path="/canva-qr-code-reviews" element={<BlogArticlePage slug="canva-qr-code-reviews" />} />
          <Route path="/connectiko-vs-canva" element={<BlogArticlePage slug="connectiko-vs-canva" />} />
          <Route path="/connectiko-vs-mobilo" element={<BlogArticlePage slug="connectiko-vs-mobilo" />} />
          <Route path="/connectiko-vs-v1ce" element={<BlogArticlePage slug="connectiko-vs-v1ce" />} />
          <Route path="/connectiko-vs-wave-connect" element={<BlogArticlePage slug="connectiko-vs-wave-connect" />} />
          <Route path="/mobilo-alternatives" element={<BlogArticlePage slug="mobilo-alternatives" />} />
          <Route path="/mobilo-customer-support-reviews" element={<BlogArticlePage slug="mobilo-customer-support-reviews" />} />
          <Route path="/mobilo-customization" element={<BlogArticlePage slug="mobilo-customization" />} />
          <Route path="/mobilo-pricing" element={<BlogArticlePage slug="mobilo-pricing" />} />
          <Route path="/v1ce-alternatives" element={<BlogArticlePage slug="v1ce-alternatives" />} />
          <Route path="/v1ce-customer-support-reviews" element={<BlogArticlePage slug="v1ce-customer-support-reviews" />} />
          <Route path="/v1ce-customization" element={<BlogArticlePage slug="v1ce-customization" />} />
          <Route path="/v1ce-pricing" element={<BlogArticlePage slug="v1ce-pricing" />} />
          <Route path="/wave-connect-alternatives" element={<BlogArticlePage slug="wave-connect-alternatives" />} />
          <Route path="/wave-connect-customer-support-reviews" element={<BlogArticlePage slug="wave-connect-customer-support-reviews" />} />
          <Route path="/wave-connect-customization" element={<BlogArticlePage slug="wave-connect-customization" />} />
          <Route path="/wave-connect-pricing" element={<BlogArticlePage slug="wave-connect-pricing" />} />
          <Route path="/connectiko-vs-blinq-events" element={<BlogArticlePage slug="connectiko-vs-blinq-events" />} />
          <Route path="/connectiko-vs-captello" element={<BlogArticlePage slug="connectiko-vs-captello" />} />
          <Route path="/connectiko-vs-cvent" element={<BlogArticlePage slug="connectiko-vs-cvent" />} />
          <Route path="/connectiko-vs-icapture" element={<BlogArticlePage slug="connectiko-vs-icapture" />} />
          <Route path="/connectiko-vs-mobly" element={<BlogArticlePage slug="connectiko-vs-mobly" />} />
          <Route path="/connectiko-vs-popl-events" element={<BlogArticlePage slug="connectiko-vs-popl-events" />} />
          <Route path="/connectiko-vs-whova" element={<BlogArticlePage slug="connectiko-vs-whova" />} />
          <Route path="/event-badge-creation" element={<BlogArticlePage slug="event-badge-creation" />} />
          <Route path="/event-badge-scanning" element={<BlogArticlePage slug="event-badge-scanning" />} />
          <Route path="/event-badge-creation-and-scanning" element={<BlogArticlePage slug="event-badge-creation-and-scanning" />} />
          <Route path="/event-lead-capture-for-agencies" element={<BlogArticlePage slug="event-lead-capture-for-agencies" />} />
          <Route path="/onsite-check-in-badge-printing" element={<BlogArticlePage slug="onsite-check-in-badge-printing" />} />
          <Route path="/ai-meeting-notes" element={<BlogArticlePage slug="ai-meeting-notes" />} />
          <Route path="/best-lead-capture-app-sales-reps" element={<BlogArticlePage slug="best-lead-capture-app-sales-reps" />} />
          <Route path="/dynamic-qr-code" element={<BlogArticlePage slug="dynamic-qr-code" />} />
          <Route path="/dynamic-vs-static-qr-code" element={<BlogArticlePage slug="dynamic-vs-static-qr-code" />} />
          <Route path="/event-lead-capture-guide" element={<BlogArticlePage slug="event-lead-capture-guide" />} />
          <Route path="/lead-capture-software-comparison-2026" element={<BlogArticlePage slug="lead-capture-software-comparison-2026" />} />
          <Route path="/lead-retrieval-app-guide-2026" element={<BlogArticlePage slug="lead-retrieval-app-guide-2026" />} />
          <Route path="/qr-code-analytics" element={<BlogArticlePage slug="qr-code-analytics" />} />
          <Route path="/qr-code-lead-capture-form-guide" element={<BlogArticlePage slug="qr-code-lead-capture-form-guide" />} />
          <Route path="/qr-code-lead-capture-how-it-works" element={<BlogArticlePage slug="qr-code-lead-capture-how-it-works" />} />
          <Route path="/qr-code-size-guide" element={<BlogArticlePage slug="qr-code-size-guide" />} />
          <Route path="/qr-code-tracking" element={<BlogArticlePage slug="qr-code-tracking" />} />
          <Route path="/trade-show-booth-ideas" element={<BlogArticlePage slug="trade-show-booth-ideas" />} />
          <Route path="/trade-show-lead-capture-app-best-tools-2026" element={<BlogArticlePage slug="trade-show-lead-capture-app-best-tools-2026" />} />
          <Route path="/trade-show-networking-follow-up-email" element={<BlogArticlePage slug="trade-show-networking-follow-up-email" />} />

          {/* Redirects for guessed/incorrect slugs found 404ing in traffic (title-derived, not real canonical slugs) */}
          <Route path="/pages/blinq-alternative-why-teams-are-switching-to-connectiko" element={<Navigate to="/pages/blinq-alternative-connectiko" replace />} />
          <Route path="/pages/connectiko-vs-hihello-vs-blinq-the-complete-comparison" element={<Navigate to="/pages/connectiko-vs-hihello-vs-blinq" replace />} />
          <Route path="/pages/flowcode-customer-support-what-users-actually-say" element={<Navigate to="/pages/flowcode-customer-support-review" replace />} />
          <Route path="/pages/how-does-a-digital-business-card-work-a-beginners-guide" element={<Navigate to="/pages/how-does-a-digital-business-card-work" replace />} />
          <Route path="/pages/connectiko-vs-mobilo-digital-business-card-comparison" element={<Navigate to="/connectiko-vs-mobilo" replace />} />
          <Route path="/pages/digital-business-card-pricing-what-you-should-expect-to-pay" element={<Navigate to="/pages/digital-business-card-pricing-what-to-expect" replace />} />
          <Route path="/pages/connectiko-vs-blinq-pricing-which-gives-you-more-for-free" element={<Navigate to="/pages/connectiko-vs-blinq-pricing" replace />} />
          <Route path="/pages/digital-business-card-for-hvac-companies" element={<Navigate to="/pages/digital-business-card-for-hvac" replace />} />
          <Route path="/pages/best-flowcode-alternatives-in-2026" element={<Navigate to="/pages/best-flowcode-alternatives-2026" replace />} />
          <Route path="/pages/connectiko-vs-flowcode-which-qr-digital-card-platform-wins-in-2026" element={<Navigate to="/pages/connectiko-vs-flowcode" replace />} />
          <Route path="/pages/add-your-digital-business-card-to-apple-wallet" element={<Navigate to="/pages/apple-wallet-business-card" replace />} />
          <Route path="/pages/connectiko-vs-blinq-digital-business-card-comparison" element={<Navigate to="/pages/connectiko-vs-blinq" replace />} />
          <Route path="/pages/connectiko-vs-popl-digital-business-card-comparison" element={<Navigate to="/pages/connectiko-vs-popl" replace />} />
          <Route path="/pages/qr-code-lead-capture-for-salesforce-teams" element={<Navigate to="/pages/salesforce-qr-lead-capture" replace />} />
          <Route path="/compare/blinq-alternative" element={<Navigate to="/pages/blinq-alternative-connectiko" replace />} />
          <Route path="/compare/wave-alternative" element={<Navigate to="/wave-connect-alternative" replace />} />
          <Route path="/compare/qrcodechimp-alternative" element={<Navigate to="/qrcodechimp-alternative" replace />} />
          <Route path="/nfc-business-card" element={<Navigate to="/nfc-compatible-digital-card" replace />} />
          <Route path="/professional-qr-code" element={<Navigate to="/branded-qr-code" replace />} />
          <Route path="/register" element={<Navigate to="/auth" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/integrations" element={<Navigate to="/platform/crm" replace />} />

          {/* Migrated static /pages/* content (single dynamic route, same URLs preserved) */}
          <Route path="/pages/:slug" element={<BlogArticlePage />} />

          {/* Comparison pages */}
          <Route path="/compare/:slug" element={<ComparePage />} />
          
          <Route path="/:slug" element={<SeoPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <UserProfileProvider>
              <CorporateAuthProvider>
                <ContactModalProvider>
                  <AINotesSocketProvider>
                    <AppContent />
                  </AINotesSocketProvider>
                </ContactModalProvider>
              </CorporateAuthProvider>
            </UserProfileProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;

